import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.handleHistoryClose);

    if (history.state && history.state.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.handleHistoryClose);
    if (this.historyAbortController) this.historyAbortController.abort();
  }

  handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
    if (!history.state || !history.state.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
    this.historyAbortController = new AbortController();
    window.addEventListener('popstate', this.handlePopState, { signal: this.historyAbortController.signal });
  };

  handleHistoryClose = () => {
    if (this.historyAbortController) this.historyAbortController.abort();
    if (history.state && history.state.cartDrawerOpen) {
      history.back();
    }
  };

  handlePopState = async () => {
    if (this.refs.dialog && this.refs.dialog.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    const itemCount = event.detail.resource ? event.detail.resource.item_count : undefined;
    this.announceCartCount(itemCount);
    this.refreshCartContents();
  };

  refreshCartContents = () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    morphSection(sectionId);
  };

  announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;
    if (!this.refs.dialog || !this.refs.dialog.open || !liveRegion || cartCount === undefined) return;
    liveRegion.textContent = Theme.translations.cart_count + ': ' + cartCount;
  }

  open() {
    this.showDialog();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms') 
        ? document.querySelector('shopify-payment-terms').shadowRoot 
        : null;
      const cta = installmentsContent ? installmentsContent.querySelector('#shopify-installments-cta') : null;
      if (cta) cta.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  updateStickyState() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > 0.5 ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
